import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Result,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  message
} from 'antd'
import {
  EditOutlined,
  PlusOutlined,
  PoweroffOutlined,
  ReloadOutlined
} from '@ant-design/icons'
import axios from 'axios'
import moment from 'moment'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { AppContext } from 'app/AppContext'
import { CUARENTENAS } from 'app_permissions/cuarentenas'
import { userHasPermission } from 'utils/userHasPermission'
import { updateClientTablePagination } from 'utils/clientTablePagination'

const maintenanceUrl = `${process.env.REACT_APP_BASE_URL_QUARANTINE}/quarantine-maintenance`

const isActive = (record) => record?.status?.code === 'ACT'
const formatDate = (value) =>
  value ? moment(Number(value)).format('DD-MM-YYYY') : 'Sin término'

const StatusTag = ({ active }) => (
  <Tag color={active ? 'green' : 'default'}>
    {active ? 'Activo' : 'Inactivo'}
  </Tag>
)

const MantenedorPorEspecie = ({ selectedSpecie }) => {
  const { rolData } = useContext(AppContext)
  const canView = userHasPermission(
    rolData?.role,
    CUARENTENAS.MANTENEDOR_VISUALIZAR
  )
  const canCreate = userHasPermission(
    rolData?.role,
    CUARENTENAS.MANTENEDOR_CREAR
  )
  const canEdit = userHasPermission(
    rolData?.role,
    CUARENTENAS.MANTENEDOR_EDITAR
  )
  const canChangeStatus = userHasPermission(
    rolData?.role,
    CUARENTENAS.MANTENEDOR_ESTADO
  )

  const [types, setTypes] = useState([])
  const [rules, setRules] = useState([])
  const [loadingTypes, setLoadingTypes] = useState(false)
  const [loadingRules, setLoadingRules] = useState(false)
  const [includeInactiveTypes, setIncludeInactiveTypes] = useState(true)
  const [includeInactiveRules, setIncludeInactiveRules] = useState(true)
  const [typeModal, setTypeModal] = useState({ visible: false, record: null })
  const [ruleModal, setRuleModal] = useState({ visible: false, record: null })
  const [typePagination, setTypePagination] = useState({
    current: 1,
    pageSize: 10
  })
  const [rulePagination, setRulePagination] = useState({
    current: 1,
    pageSize: 10
  })
  const [saving, setSaving] = useState(false)
  const [typeForm] = Form.useForm()
  const [ruleForm] = Form.useForm()

  const loadTypes = useCallback(async () => {
    setLoadingTypes(true)
    try {
      const response = await axios.get(`${maintenanceUrl}/types`, {
        params: {
          code_specie: selectedSpecie.code,
          include_inactive: includeInactiveTypes
        }
      })
      setTypes(response.data || [])
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          'No fue posible cargar los tipos de cuarentena'
      )
    } finally {
      setLoadingTypes(false)
    }
  }, [includeInactiveTypes, selectedSpecie.code])

  const loadRules = useCallback(async () => {
    setLoadingRules(true)
    try {
      const response = await axios.get(`${maintenanceUrl}/movement-rules`, {
        params: {
          code_specie: selectedSpecie.code,
          include_inactive: includeInactiveRules
        }
      })
      setRules(response.data || [])
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          'No fue posible cargar los cruces de cuarentena'
      )
    } finally {
      setLoadingRules(false)
    }
  }, [includeInactiveRules, selectedSpecie.code])

  useEffect(() => {
    if (canView) loadTypes()
  }, [canView, loadTypes])

  useEffect(() => {
    if (canView) loadRules()
  }, [canView, loadRules])

  const handleTypeTableChange = (selection) => {
    setTypePagination((previous) =>
      updateClientTablePagination(previous, selection)
    )
  }

  const handleRuleTableChange = (selection) => {
    setRulePagination((previous) =>
      updateClientTablePagination(previous, selection)
    )
  }

  const activeTypes = useMemo(() => types.filter(isActive), [types])

  const openTypeModal = (record = null) => {
    setTypeModal({ visible: true, record })
    typeForm.setFieldsValue(
      record
        ? { code: record.code, name: record.name }
        : { code: undefined, name: undefined }
    )
  }

  const saveType = async () => {
    try {
      const values = await typeForm.validateFields()
      setSaving(true)
      if (typeModal.record) {
        await axios.patch(
          `${maintenanceUrl}/types/${typeModal.record.id}`,
          values,
          { params: { code_specie: selectedSpecie.code } }
        )
      } else {
        await axios.post(`${maintenanceUrl}/types`, {
          ...values,
          code_specie: selectedSpecie.code
        })
      }
      message.success(
        typeModal.record
          ? 'Tipo de cuarentena actualizado'
          : 'Tipo de cuarentena creado'
      )
      setTypeModal({ visible: false, record: null })
      typeForm.resetFields()
      await loadTypes()
    } catch (error) {
      if (!error?.errorFields) {
        message.error(
          error?.response?.data?.message ||
            'No fue posible guardar el tipo de cuarentena'
        )
      }
    } finally {
      setSaving(false)
    }
  }

  const changeTypeStatus = async (record) => {
    try {
      if (isActive(record)) {
        await axios.delete(`${maintenanceUrl}/types/${record.id}`, { params: { code_specie: selectedSpecie.code } })
      } else {
        await axios.patch(
          `${maintenanceUrl}/types/${record.id}/status`,
          { is_active: true },
          { params: { code_specie: selectedSpecie.code } }
        )
      }
      message.success(
        `Tipo de cuarentena ${isActive(record) ? 'inactivado' : 'activado'}`
      )
      await Promise.all([loadTypes(), loadRules()])
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          'No fue posible cambiar el estado del tipo'
      )
    }
  }

  const openRuleModal = (record = null) => {
    setRuleModal({ visible: true, record })
    ruleForm.setFieldsValue(
      record
        ? {
            origin_type_quarantine_id: record.origin_type_quarantine_id,
            destination_type_quarantine_id:
              record.destination_type_quarantine_id,
            valid_from: moment(Number(record.valid_from)),
            valid_to: record.valid_to ? moment(Number(record.valid_to)) : null,
            description: record.description
          }
        : {
            origin_type_quarantine_id: undefined,
            destination_type_quarantine_id: undefined,
            valid_from: moment().startOf('day'),
            valid_to: null,
            description: undefined
          }
    )
  }

  const saveRule = async () => {
    try {
      const values = await ruleForm.validateFields()
      const payload = {
        pyramid_specie_id: Number(selectedSpecie.id),
        origin_type_quarantine_id: values.origin_type_quarantine_id,
        destination_type_quarantine_id: values.destination_type_quarantine_id,
        valid_from: values.valid_from.startOf('day').valueOf(),
        valid_to: values.valid_to
          ? values.valid_to.endOf('day').valueOf()
          : null,
        description: values.description?.trim() || null
      }
      setSaving(true)
      if (ruleModal.record) {
        await axios.patch(
          `${maintenanceUrl}/movement-rules/${ruleModal.record.id}`,
          payload
        )
      } else {
        await axios.post(`${maintenanceUrl}/movement-rules`, payload)
      }
      message.success(
        ruleModal.record ? 'Cruce actualizado' : 'Cruce creado'
      )
      setRuleModal({ visible: false, record: null })
      ruleForm.resetFields()
      await loadRules()
    } catch (error) {
      if (!error?.errorFields) {
        message.error(
          error?.response?.data?.message || 'No fue posible guardar el cruce'
        )
      }
    } finally {
      setSaving(false)
    }
  }

  const changeRuleStatus = async (record) => {
    try {
      if (isActive(record)) {
        await axios.delete(`${maintenanceUrl}/movement-rules/${record.id}`)
      } else {
        await axios.patch(
          `${maintenanceUrl}/movement-rules/${record.id}/status`,
          { is_active: true }
        )
      }
      message.success(`Cruce ${isActive(record) ? 'inactivado' : 'activado'}`)
      await loadRules()
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          'No fue posible cambiar el estado del cruce'
      )
    }
  }

  const typeColumns = [
    { title: 'Código', dataIndex: 'code', width: 130 },
    { title: 'Tipo de cuarentena', dataIndex: 'name' },
    {
      title: 'Estado',
      key: 'status',
      width: 120,
      render: (_, record) => <StatusTag active={isActive(record)} />
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 190,
      render: (_, record) => (
        <Space>
          {canEdit && (
            <Button
              icon={<EditOutlined />}
              onClick={() => openTypeModal(record)}
            >
              Editar
            </Button>
          )}
          {canChangeStatus && (
            <Popconfirm
              title={`¿Deseas ${
                isActive(record) ? 'inactivar' : 'activar'
              } este tipo?`}
              onConfirm={() => changeTypeStatus(record)}
              okText='Sí'
              cancelText='No'
            >
              <Button icon={<PoweroffOutlined />}>
                {isActive(record) ? 'Inactivar' : 'Activar'}
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ]

  const ruleColumns = [
    {
      title: 'Origen',
      dataIndex: ['originTypeQuarantine', 'name'],
      width: 230
    },
    {
      title: 'Destino',
      dataIndex: ['destinationTypeQuarantine', 'name'],
      width: 230
    },
    {
      title: 'Vigente desde',
      dataIndex: 'valid_from',
      render: formatDate,
      width: 130
    },
    {
      title: 'Vigente hasta',
      dataIndex: 'valid_to',
      render: formatDate,
      width: 130
    },
    { title: 'Descripción', dataIndex: 'description' },
    {
      title: 'Estado',
      key: 'status',
      width: 110,
      render: (_, record) => <StatusTag active={isActive(record)} />
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 190,
      render: (_, record) => (
        <Space>
          {canEdit && (
            <Button
              icon={<EditOutlined />}
              onClick={() => openRuleModal(record)}
              disabled={!isActive(record)}
            >
              Editar
            </Button>
          )}
          {canChangeStatus && (
            <Popconfirm
              title={`¿Deseas ${
                isActive(record) ? 'inactivar' : 'activar'
              } este cruce?`}
              onConfirm={() => changeRuleStatus(record)}
              okText='Sí'
              cancelText='No'
            >
              <Button icon={<PoweroffOutlined />}>
                {isActive(record) ? 'Inactivar' : 'Activar'}
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ]

  if (!canView) {
    return (
      <Result
        status='403'
        title='Sin permisos'
        subTitle='No tienes permisos para visualizar el mantenedor de cuarentenas.'
      />
    )
  }

  const typeToolbar = (
    <div className='quarantine-maintenance__toolbar'>
      <Space>
        <span>Mostrar inactivos</span>
        <Switch
          checked={includeInactiveTypes}
          onChange={setIncludeInactiveTypes}
        />
        <Button icon={<ReloadOutlined />} onClick={loadTypes}>
          Actualizar
        </Button>
      </Space>
      {canCreate && (
        <Button type='primary' icon={<PlusOutlined />} onClick={() => openTypeModal()}>
          Nuevo tipo
        </Button>
      )}
    </div>
  )

  const ruleToolbar = (
    <div className='quarantine-maintenance__toolbar'>
      <Space>
        <span>Mostrar inactivos</span>
        <Switch
          checked={includeInactiveRules}
          onChange={setIncludeInactiveRules}
        />
        <Button icon={<ReloadOutlined />} onClick={loadRules}>
          Actualizar
        </Button>
      </Space>
      {canCreate && (
        <Space>
          <Button onClick={() => openTypeModal()}>Crear tipo</Button>
          <Button
            type='primary'
            icon={<PlusOutlined />}
            onClick={() => openRuleModal()}
          >
            Nuevo cruce
          </Button>
        </Space>
      )}
    </div>
  )

  return (
    <div className='quarantine-maintenance'>
      <div className='quarantine-maintenance__header'>
        <h2>Movimientos permitidos de cuarentena</h2>
        <p>
          Los tipos de cuarentena son compartidos por todas las especies.
          Los cruces que se muestran y guardan corresponden a {selectedSpecie.name_specie}.
        </p>
      </div>
      <Card>
        <Tabs defaultActiveKey='rules'>
          <Tabs.TabPane tab='Cruces permitidos' key='rules'>
            {ruleToolbar}
            <Table
              rowKey='id'
              loading={loadingRules}
              columns={ruleColumns}
              dataSource={rules}
              scroll={{ x: 1250 }}
              pagination={{
                ...rulePagination,
                showSizeChanger: true
              }}
              onChange={handleRuleTableChange}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab='Tipos compartidos' key='types'>
            {typeToolbar}
            <Table
              rowKey='id'
              loading={loadingTypes}
              columns={typeColumns}
              dataSource={types}
              scroll={{ x: 700 }}
              pagination={{
                ...typePagination,
                showSizeChanger: true
              }}
              onChange={handleTypeTableChange}
            />
          </Tabs.TabPane>
        </Tabs>
      </Card>

      <Modal
        title={typeModal.record ? 'Editar tipo compartido' : 'Nuevo tipo compartido'}
        visible={typeModal.visible}
        onOk={saveType}
        onCancel={() => {
          setTypeModal({ visible: false, record: null })
          typeForm.resetFields()
        }}
        confirmLoading={saving}
        okText='Guardar'
        cancelText='Cancelar'
        destroyOnClose
      >
        <p>Este tipo estará disponible para todas las especies. Modificar el catálogo requiere permisos en todas ellas.</p>
        <Form form={typeForm} layout='vertical'>
          <Form.Item
            name='code'
            label='Código'
            rules={[{ required: true, message: 'Ingresa el código' }]}
          >
            <Input maxLength={50} placeholder='Ej.: RVMS' />
          </Form.Item>
          <Form.Item
            name='name'
            label='Nombre'
            rules={[{ required: true, message: 'Ingresa el nombre' }]}
          >
            <Input maxLength={100} placeholder='Ej.: Reovirus Salmonella MS' />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={ruleModal.record ? 'Editar cruce permitido' : 'Nuevo cruce permitido'}
        visible={ruleModal.visible}
        onOk={saveRule}
        onCancel={() => {
          setRuleModal({ visible: false, record: null })
          ruleForm.resetFields()
        }}
        confirmLoading={saving}
        okText='Guardar'
        cancelText='Cancelar'
        width={650}
        destroyOnClose
      >
        <Form form={ruleForm} layout='vertical'>
          <Form.Item
            name='origin_type_quarantine_id'
            label='Tipo de cuarentena origen'
            rules={[{ required: true, message: 'Selecciona el origen' }]}
          >
            <SelectWithTypes types={activeTypes} />
          </Form.Item>
          <Form.Item
            name='destination_type_quarantine_id'
            label='Tipo de cuarentena destino'
            rules={[{ required: true, message: 'Selecciona el destino' }]}
          >
            <SelectWithTypes types={activeTypes} />
          </Form.Item>
          <Space size='large' align='start'>
            <Form.Item
              name='valid_from'
              label='Vigente desde'
              rules={[{ required: true, message: 'Indica la fecha inicial' }]}
            >
              <DatePicker format='DD-MM-YYYY' />
            </Form.Item>
            <Form.Item name='valid_to' label='Vigente hasta'>
              <DatePicker format='DD-MM-YYYY' />
            </Form.Item>
          </Space>
          <Form.Item name='description' label='Descripción opcional'>
            <Input.TextArea maxLength={500} rows={3} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

const SelectWithTypes = ({ types, ...props }) => {
  return (
    <Select
      {...props}
      showSearch
      optionFilterProp='children'
      placeholder='Selecciona un tipo'
    >
      {types.map((type) => (
        <Select.Option key={type.id} value={Number(type.id)}>
          {type.name}
        </Select.Option>
      ))}
    </Select>
  )
}

const MantenedorCuarentenas = () => {
  const { rolData } = useContext(AppContext)
  const canView = userHasPermission(rolData?.role, CUARENTENAS.MANTENEDOR_VISUALIZAR)
  const [species, setSpecies] = useState([])
  const [selectedCode, setSelectedCode] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const loadSpecies = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BASE_URL_QUARANTINE}/quarantine/data-master/species`
      )
      const options = [...data].sort((a, b) => a.code.localeCompare(b.code))
      setSpecies(options)
      setSelectedCode((previous) =>
        options.some((item) => item.code === previous)
          ? previous
          : options.find((item) => item.code === '003')?.code || options[0]?.code || null
      )
    } catch (error) {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => {
    if (canView) loadSpecies()
  }, [canView, loadSpecies])
  const selectedSpecie = species.find((item) => item.code === selectedCode)
  if (!canView) return <Result status='403' title='Sin permisos para visualizar el mantenedor' />
  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <label htmlFor='quarantine-maintenance-specie'>Especie</label>
        <Select
          id='quarantine-maintenance-specie'
          aria-label='Especie'
          style={{ minWidth: 220 }}
          loading={loading}
          disabled={loading || species.length === 0}
          value={selectedCode}
          onChange={setSelectedCode}
          options={species.map((item) => ({ label: item.name_specie, value: item.code }))}
        />
        {error && <Button onClick={loadSpecies}>Reintentar carga de especies</Button>}
      </Space>
      {error && <p role='alert'>No fue posible cargar las especies.</p>}
      {selectedSpecie && (
        <MantenedorPorEspecie key={selectedCode} selectedSpecie={selectedSpecie} />
      )}
    </>
  )
}

export default MantenedorCuarentenas
